import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Bike,
  Check,
  Clock,
  Package,
  PackageCheck,
} from "lucide-react";
import {
  partnerStageTranslationKey,
  type PartnerOrderStage,
} from "../../lib/partnerOrders";
import { useI18n } from "../../i18n";

/** Partner HQ stages — same canonical labels as Orders / Partner app. */
export const PARTNER_FLOW_STAGES: PartnerOrderStage[] = [
  "pending",
  "processing",
  "ready",
  "in_delivery",
  "completed",
];

export interface PartnerFlowStepCount {
  stage: PartnerOrderStage;
  count: number;
}

const ICON_CLASS = "overview-flow__icon";

const STAGE_ICONS: Record<PartnerOrderStage, ReactNode> = {
  pending: <Clock className={ICON_CLASS} strokeWidth={2.25} />,
  processing: <Package className={ICON_CLASS} strokeWidth={2.25} />,
  ready: <PackageCheck className={ICON_CLASS} strokeWidth={2.25} />,
  in_delivery: <Bike className={ICON_CLASS} strokeWidth={2.25} />,
  completed: <Check className={ICON_CLASS} strokeWidth={2.25} />,
  cancelled: <Clock className={ICON_CLASS} strokeWidth={2.25} />,
};

function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    const diff = value - prev;
    if (diff === 0) {
      setDisplay(value);
      return;
    }
    const duration = 420;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(prev + diff * eased));
      if (progress < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    prevRef.current = value;
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [value]);

  return <>{display}</>;
}

interface PartnerLiveOrderFlowProps {
  steps: PartnerFlowStepCount[];
  selectedStage: PartnerOrderStage | null;
  onSelectStage: (stage: PartnerOrderStage | null) => void;
  loading?: boolean;
}

/** Client Dashboard LiveOrderFlow visual — Partner stage labels only. */
export function PartnerLiveOrderFlow({
  steps,
  selectedStage,
  onSelectStage,
  loading = false,
}: PartnerLiveOrderFlowProps) {
  const { t } = useI18n();
  const countByStage = Object.fromEntries(steps.map((s) => [s.stage, s])) as Record<
    PartnerOrderStage,
    PartnerFlowStepCount
  >;

  const totalActive = steps
    .filter((s) => s.stage !== "completed")
    .reduce((sum, s) => sum + s.count, 0);

  return (
    <section
      className={`overview-flow overview-flow--hero${loading ? " overview-flow--loading" : ""}`}
    >
      <div className="overview-flow__header">
        <div className="overview-flow__header-content">
          <div className="overview-flow__title-row">
            <h2 className="overview-flow__title">{t("overview.flow.title")}</h2>
            <span className="overview-flow__live">
              <span className="overview-flow__live-dot" aria-hidden />
              {t("overview.flow.liveBadge")}
            </span>
          </div>
          <p className="overview-flow__hint">{t("overview.flow.hint")}</p>
        </div>
        <div className="overview-flow__summary">
          <span className="overview-flow__summary-value">
            <AnimatedNumber value={totalActive} />
          </span>
          <span className="overview-flow__summary-label">
            {t("overview.flow.activeNow")}
          </span>
        </div>
      </div>

      <ol className="overview-flow__track" aria-label={t("overview.flow.aria")}>
        {PARTNER_FLOW_STAGES.map((stage, index) => {
          const data = countByStage[stage] ?? { stage, count: 0 };
          const isSelected = selectedStage === stage;
          const hasOrders = data.count > 0;
          const isLast = index === PARTNER_FLOW_STAGES.length - 1;
          const nextHasOrders =
            !isLast &&
            (countByStage[PARTNER_FLOW_STAGES[index + 1]!]?.count ?? 0) > 0;
          const connectorActive = hasOrders || nextHasOrders;
          const stageLabel = t(partnerStageTranslationKey(stage));

          return (
            <li key={stage} className="overview-flow__item">
              <button
                type="button"
                onClick={() => onSelectStage(isSelected ? null : stage)}
                aria-pressed={isSelected}
                aria-label={`${stageLabel}, ${data.count} ${t("overview.flow.ordersLabel")}`}
                className={[
                  "overview-flow__step",
                  hasOrders ? "overview-flow__step--active" : "",
                  isSelected ? "overview-flow__step--selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className="overview-flow__step-badge" aria-hidden>
                  <AnimatedNumber value={data.count} />
                </span>
                <span className="overview-flow__step-icon" aria-hidden>
                  {STAGE_ICONS[stage]}
                </span>
                <span className="overview-flow__step-label">{stageLabel}</span>
              </button>

              {!isLast ? (
                <div
                  className={`overview-flow__connector ${
                    connectorActive ? "overview-flow__connector--on" : ""
                  }`}
                  aria-hidden
                >
                  <span className="overview-flow__connector-line" />
                  {connectorActive ? (
                    <span className="overview-flow__connector-ball" />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
